import { AIActions, selectAIGenerating } from '@store/reducers/ai'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  clearAiNotifications,
  notifyAiCouldNotFinish,
  notifyAiStillWorking,
} from '@/hooks/ai/actionCreators'

/** How long a turn runs before we reassure the user it is "Still working…". */
const STILL_WORKING_MS = 20_000
/** After this the turn is anomalously long — surface "Taking longer than usual…". */
const COULD_NOT_FINISH_MS = 60_000

/**
 * Renders nothing; drives the timed AI-progress toasts (OCT-4826). While the
 * assistant works it shows "Still working… [Stop]" after a delay and "Taking
 * longer than usual… [Stop]" if it drags on. Everything is torn down (timers +
 * toasts) the moment the turn ends, so a fast turn never shows either.
 *
 * Keyed off the whole-turn `generating` flag (not the mutation-scoped canvas
 * lock): these toasts should reassure the user on ANY long turn, including a
 * read-only one that never touches the canvas.
 */
export const AiWorkingNotifier: React.FC = () => {
  const dispatch = useDispatch()
  const generating = useSelector(selectAIGenerating)

  useEffect(() => {
    if (!generating) {
      return undefined
    }

    const onStop = () => dispatch(AIActions.stopMessage())

    const stillWorkingTimer = window.setTimeout(() => {
      dispatch(clearAiNotifications())
      dispatch(notifyAiStillWorking(onStop))
    }, STILL_WORKING_MS)

    const couldNotFinishTimer = window.setTimeout(() => {
      dispatch(clearAiNotifications())
      dispatch(notifyAiCouldNotFinish(onStop))
    }, COULD_NOT_FINISH_MS)

    return () => {
      window.clearTimeout(stillWorkingTimer)
      window.clearTimeout(couldNotFinishTimer)
      dispatch(clearAiNotifications())
    }
  }, [generating, dispatch])

  return null
}
